import { MouseEvent } from 'react';
import clsx from 'clsx';

import {
  AMPLIFY_ORANGE,
  getDomainLink,
  getProgramColor,
  handleSpaceClick,
} from 'shared/lib/utils';

import styles from './styles.module.scss';

type Props = {
  domain: string;
  subject?: string;
  programLink?: string;
  programAssetId?: string;
  asButton?: boolean;
  onClick?: (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => Promise<void> | void;
  onContextMenu?: (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => Promise<void> | void;
  domainNameClassNames?: string;
  domainButtonClassNames?: string;
};

export const DomainBadge = ({
  domain,
  subject,
  programLink,
  programAssetId,
  asButton,
  onClick,
  onContextMenu,
  domainNameClassNames,
  domainButtonClassNames,
}: Props) => {
  const domainLink =
    programLink ?? getDomainLink(subject ?? domain, programAssetId);
  const programColor = getProgramColor(domain);

  const handleClick = async (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    e.preventDefault();
    /* To prevent a XApiEventKeys.goToLessonSelected event being sent,
       which leads to false negative in related unit test */
    e.stopPropagation();

    await onClick?.(e);

    window.open(domainLink, '_self');
  };

  const handleContextMenu = async (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    e.stopPropagation(); // to prevent from triggering events for parent elements

    await onContextMenu?.(e);
  };

  const domainBadgeClassNames = clsx(
    domainButtonClassNames,
    styles.root,
    styles[domain.split(' ').join('-').toLowerCase()],
  );

  const getColoredBadgeBackground = () => {
    return (
      <span
        className={styles.coloredBackground}
        style={{ backgroundColor: programColor ?? AMPLIFY_ORANGE }}
      />
    );
  };

  if (domain === 'Tutorial' || domain === 'Custom') {
    return <div className={domainBadgeClassNames}>{domain}</div>;
  }

  if (!domain) {
    return null;
  }

  return asButton ? (
    <button
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      className={domainBadgeClassNames}
    >
      {getColoredBadgeBackground()}
      <span className={clsx(styles.domainName, domainNameClassNames)}>
        {domain}
      </span>
    </button>
  ) : (
    <a
      href={domainLink}
      className={domainBadgeClassNames}
      onKeyDown={handleSpaceClick}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      {getColoredBadgeBackground()}
      <span className={clsx(styles.domainName, domainNameClassNames)}>
        {domain}
      </span>
    </a>
  );
};
